import React from 'react';
import { Province, Town } from '../api/locations';
import { Category } from '../types/contractor';

interface SidebarFiltersProps {
  onClearAll: () => void;
  onProvinceChange: (provinceId: number | undefined) => void;
  onCategoryIdsChange: (categoryIds: number[]) => void;
  onTownChange: (townId: number | undefined) => void;

  provinces: Province[];
  categories: Category[];
  towns: Town[];
  selectedCategoryIds: number[];
  selectedProvinceId?: number;
  selectedTownId?: number;
}

const SidebarFilters: React.FC<SidebarFiltersProps> = ({
  onClearAll,
  onProvinceChange,
  onCategoryIdsChange,
  onTownChange,
  provinces,
  categories,
  towns,
  selectedCategoryIds,
  selectedProvinceId,
  selectedTownId,
}) => {
  const toggleCategory = (id: number) => {
    const next = selectedCategoryIds.includes(id)
      ? selectedCategoryIds.filter(c => c !== id)
      : [...selectedCategoryIds, id];
    onCategoryIdsChange(next);
  };

  return (
    <aside className="w-72 flex-shrink-0 hidden lg:block">
      <div className="bg-surface-container-lowest rounded-xl p-stack-lg shadow-sm border border-outline-variant/30 sticky top-24">
        <div className="flex items-center justify-between mb-stack-lg">
          <h2 className="font-headline-sm text-headline-sm text-primary">Filters</h2>
          <button className="text-label-bold font-label-bold text-secondary hover:underline" onClick={onClearAll}>Clear all</button>
        </div>
        <div className="space-y-stack-xl">
          {/* Province */}
          <section>
            <label className="font-label-bold text-label-bold block mb-stack-sm text-on-surface-variant">PROVINCE</label>
            <select
              className="w-full p-2 bg-surface-container border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-body-md font-body-md"
              value={selectedProvinceId ?? ''}
              onChange={(e) => onProvinceChange(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">All provinces</option>
              {provinces.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </section>

          {/* Town */}
          <section>
            <label className="font-label-bold text-label-bold block mb-stack-sm text-on-surface-variant">TOWN</label>
            <select
              className="w-full p-2 bg-surface-container border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-body-md font-body-md disabled:opacity-50"
              value={selectedTownId ?? ''}
              disabled={towns.length === 0}
              onChange={(e) => onTownChange(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">{towns.length === 0 ? 'Select a province first' : 'All towns'}</option>
              {towns.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </section>

          {/* Categories — multi-select checkboxes */}
          <section>
            <label className="font-label-bold text-label-bold block mb-stack-sm text-on-surface-variant">CATEGORIES</label>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {categories.map(c => (
                <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCategoryIds.includes(c.id)}
                    onChange={() => toggleCategory(c.id)}
                    className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary"
                  />
                  <span className="text-body-md font-body-md text-on-surface">{c.name}</span>
                </label>
              ))}
            </div>
            {selectedCategoryIds.length > 0 && (
              <p className="text-xs text-on-surface-variant mt-2">
                {selectedCategoryIds.length} selected — contractors must match all
              </p>
            )}
          </section>

        </div>
      </div>
    </aside>
  );
};

export default SidebarFilters;
